import { lsStorage } from "src/Utilities/storage";
import { useSyncExternalStore } from "react";

/**
 * State behind the "Set password" prompt for an account whose password was
 * generated at sign-up and never shown to its owner.
 *
 * Two separate things live here:
 *
 * - `retained` is the generated password itself, kept so the prompt can change it
 *   directly. Module state rather than storage on purpose: it is a real
 *   credential, and writing it to sessionStorage would leave it sitting in the
 *   browser. Client-side navigation preserves module state, so it survives the
 *   trip to the checkout, and it dies with the tab.
 * - `isOpen` is whether the prompt is showing, so the header badge can reopen it.
 *
 * Once `retained` is gone — a reload, or a later visit — the prompt falls back to
 * the emailed-code reset, because the backend requires a current password for a
 * direct change no matter what `is_password_randomly_generated` says.
 */
interface IRetainedCredentials {
    email: string;
    currentPassword: string;
}

let retained: IRetainedCredentials | null = null;
let isOpen = false;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

/** Remember the generated password so the prompt can change it without a code. */
export const retainGeneratedPassword = (credentials: IRetainedCredentials): void => {
    retained = credentials;
    notify();
};

export const openPasswordPrompt = (): void => {
    isOpen = true;
    notify();
};

export const closePasswordPrompt = (): void => {
    isOpen = false;
    notify();
};

/** Called once a real password exists, so nothing is left holding the old one. */
export const clearRetainedPassword = (): void => {
    retained = null;
    isOpen = false;
    notify();
};

interface IPromptState {
    isOpen: boolean;
    retained: IRetainedCredentials | null;
    /** Emails known to have had a real password set from this browser. */
    setEmails: string[];
}

let snapshot: IPromptState = { isOpen: false, retained: null, setEmails: [] };

const getSnapshot = (): IPromptState => {
    // Rebuilt only when something actually changed: useSyncExternalStore compares
    // snapshots by identity and would loop forever on a fresh object each call.
    if (
        snapshot.isOpen !== isOpen ||
        snapshot.retained !== retained ||
        snapshot.setEmails !== setEmails
    ) {
        snapshot = { isOpen, retained, setEmails };
    }
    return snapshot;
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const SERVER_SNAPSHOT: IPromptState = { isOpen: false, retained: null, setEmails: [] };

export const usePasswordPrompt = (): IPromptState =>
    useSyncExternalStore(subscribe, getSnapshot, () => SERVER_SNAPSHOT);

/**
 * Accounts known to have had a real password set from this browser.
 *
 * A second guard on the "Set password" prompt, independent of
 * `is_password_randomly_generated`. That flag is the backend's own record and is
 * the right signal, but a prompt that will not go away is worse than one that
 * disappears slightly early — so a set we performed ourselves also suppresses it.
 *
 * Keyed by email rather than account id, because both places that set a password
 * have the email to hand but only one of them has the loaded account: the sign-up
 * sheet does it before the account is fetched. Lower-cased, since an email is not
 * case sensitive and the two paths spell it differently.
 *
 * Being localStorage, it is per-device: it does not travel, and it is a belt to
 * the flag's braces rather than a replacement for it.
 */
const passwordSetAccounts = lsStorage("passwordSetAccounts");

const normaliseEmail = (email?: string | null): string => (email ?? "").trim().toLowerCase();

const readStoredEmails = (): string[] => {
    try {
        const stored = passwordSetAccounts.getItem();
        return Array.isArray(stored) ? stored : [];
    } catch {
        // Blocked or unparseable storage: fall back to trusting the flag alone.
        return [];
    }
};

/**
 * Mirrored in module state as well as localStorage, so the header can subscribe.
 * localStorage is not reactive: a component that reads it during render only
 * notices a change when something else happens to re-render it, which is how the
 * badge survived a password being set.
 */
let setEmails: string[] = readStoredEmails();

export const markPasswordSet = (email?: string | null): void => {
    const key = normaliseEmail(email);
    if (!key || setEmails.includes(key)) return;
    setEmails = [...setEmails, key];
    try {
        passwordSetAccounts.setItem(setEmails);
    } catch {
        // The in-memory copy still holds for this tab.
    }
    notify();
};

export const hasSetPasswordLocally = (email?: string | null): boolean => {
    const key = normaliseEmail(email);
    return !!key && setEmails.includes(key);
};

/**
 * Subscribing form of hasSetPasswordLocally, for anything that must hide itself
 * the moment a password is set — the header badge above all.
 */
export const useHasSetPasswordLocally = (email?: string | null): boolean => {
    const { setEmails: known } = usePasswordPrompt();
    const key = normaliseEmail(email);
    return !!key && known.includes(key);
};
